import type { ComponentProps } from 'react'
import { useEffect } from 'react'
import { fetchPlaceByName } from 'actions/PlaceActions'
import useIsomorphicLayoutEffect from 'hooks/useIsomorphicLayoutEffect'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { type TripDrawerAnimation } from 'tripPlanner/components/GenericDrawer/TripDrawer'
import { useRightPanelTripDrawerPresentation } from 'tripPlanner/components/GenericDrawer/TripDrawerPresentationContext'
import { useGetPlaceInfo } from 'tripPlanner/hooks/api/chat'
import type { GetPlaceInfoResult, TripItineraryData } from 'tripPlanner/api/types'
import TripPlanDetailDrawer from './TripPlanDetailDrawer'
import PlaceInfoDrawer from './PlaceInfoDrawer'
import PendingPlaceDrawer from './PendingPlaceDrawer'

interface Props {
  isOpen: boolean
  onClose: () => void
  onAfterClose?: () => void
  onBack?: () => void
  drawerEntryId?: string
  onPendingChange?: (entryId: string, isPending: boolean) => void
  renderWhenReady?: boolean
  animation?: TripDrawerAnimation
  googlePlaceId: string
  placeName: string
  itineraryData?: TripItineraryData
  onSendMessage?: (message: string) => void
  chatId?: number
}

type PlaceInfoForClassification = GetPlaceInfoResult & {
  primaryType?: string
  types?: Array<string>
  countryName?: string
  country?: string
  region?: string
}

const DESTINATION_PLACE_TYPES = new Set([
  'administrative_area_level_1',
  'administrative_area_level_2',
  'administrative_area_level_3',
  'colloquial_area',
  'country',
  'locality',
  'neighborhood',
  'political',
  'postal_town',
  'sublocality',
  'sublocality_level_1',
  'tourist_destination',
])

const DESTINATION_CATEGORIES = new Set([
  'city',
  'country',
  'destination',
  'locality',
  'neighbourhood',
  'neighborhood',
  'region',
  'state',
  'suburb',
  'tourist_destination',
])

const POI_PLACE_TYPES = new Set([
  'bar',
  'cafe',
  'establishment',
  'food',
  'lodging',
  'museum',
  'park',
  'point_of_interest',
  'restaurant',
  'store',
  'tourist_attraction',
])

const DESTINATION_PLACE_LEVELS = new Set<App.PlaceType>([
  'city',
  'continent',
  'country',
  'high_level_region',
  'multi_city_vicinity',
  'province_state',
])

function normalizeClassificationValue(value: string) {
  return value.trim().toLowerCase().replace(/\s+/g, '_')
}

function getPlaceTypes(placeInfo: PlaceInfoForClassification): Array<string> {
  const types = placeInfo.types ?? []
  return placeInfo.primaryType ? [placeInfo.primaryType, ...types] : types
}

function isDestinationPlace(place: App.Place | null | undefined) {
  return !!place?.type && DESTINATION_PLACE_LEVELS.has(place.type)
}

function getClassificationSignals(placeInfo: GetPlaceInfoResult | undefined) {
  if (!placeInfo) {
    return {
      hasDestinationType: false,
      hasPoiType: false,
      hasDestinationCategory: false,
    }
  }

  const typedPlaceInfo = placeInfo as PlaceInfoForClassification
  const normalizedTypes = getPlaceTypes(typedPlaceInfo).map(normalizeClassificationValue)
  const hasPoiType = normalizedTypes.some(type => POI_PLACE_TYPES.has(type))
  const hasDestinationType = normalizedTypes.some(type => DESTINATION_PLACE_TYPES.has(type))
  const normalizedCategory = typedPlaceInfo.category ?
      normalizeClassificationValue(typedPlaceInfo.category) :
    undefined
  const hasDestinationCategory = !!normalizedCategory && DESTINATION_CATEGORIES.has(normalizedCategory)

  return {
    hasDestinationType,
    hasPoiType,
    hasDestinationCategory,
  }
}

type SmartPlaceDrawerClassification = 'destination' | 'placeInfo' | 'pending'

export function classifySmartPlaceDrawer(
  placeInfo: GetPlaceInfoResult | undefined,
  destinationPlace: App.Place | null | undefined,
  isDestinationLookupPending: boolean,
): SmartPlaceDrawerClassification {
  const {
    hasDestinationType,
    hasPoiType,
    hasDestinationCategory,
  } = getClassificationSignals(placeInfo)

  if (hasPoiType) return 'placeInfo'
  if (hasDestinationType || hasDestinationCategory) return 'destination'
  if (isDestinationLookupPending) return 'pending'
  if (isDestinationPlace(destinationPlace)) return 'destination'

  return 'placeInfo'
}

export function shouldOpenDestinationDetailDrawer(
  placeInfo: GetPlaceInfoResult | undefined,
  destinationPlace?: App.Place | null,
): boolean {
  return classifySmartPlaceDrawer(placeInfo, destinationPlace, false) === 'destination'
}

function getCountryName(placeInfo: GetPlaceInfoResult | undefined) {
  if (!placeInfo) return undefined

  const typedPlaceInfo = placeInfo as PlaceInfoForClassification
  return typedPlaceInfo.countryName ?? typedPlaceInfo.country ?? typedPlaceInfo.region
}

function SmartPlaceDrawer({
  isOpen,
  onClose,
  onAfterClose,
  onBack,
  drawerEntryId,
  onPendingChange,
  renderWhenReady = true,
  animation,
  googlePlaceId,
  placeName,
  itineraryData,
  onSendMessage,
  chatId,
}: Props) {
  const dispatch = useAppDispatch()
  const drawerPresentation = useRightPanelTripDrawerPresentation()
  const showImmediatePendingFeedback = drawerPresentation === 'modal'
  const destinationPlace = useAppSelector(state => state.destination.locationToPlace[placeName])
  const isDestinationLookupLoading = useAppSelector(state => !!state.destination.placesLoading[placeName])
  const {
    data: placeInfo,
    isLoading,
  } = useGetPlaceInfo(isOpen ? googlePlaceId : undefined, 'preview')
  const {
    hasDestinationType,
    hasPoiType,
    hasDestinationCategory,
  } = getClassificationSignals(placeInfo)
  const hasImmediateClassification = hasPoiType || hasDestinationType || hasDestinationCategory
  const isDestinationLookupPending = isOpen &&
    destinationPlace === undefined &&
    !hasImmediateClassification
  const drawerClassification = classifySmartPlaceDrawer(
    placeInfo,
    destinationPlace,
    isDestinationLookupPending,
  )
  const isPending = (isLoading && !placeInfo) || drawerClassification === 'pending'

  useEffect(() => {
    if (
      !isOpen ||
      !placeName ||
      isLoading ||
      destinationPlace !== undefined ||
      isDestinationLookupLoading ||
      hasImmediateClassification
    ) {
      return
    }

    dispatch(fetchPlaceByName(placeName))
  }, [
    destinationPlace,
    dispatch,
    hasImmediateClassification,
    isDestinationLookupLoading,
    isLoading,
    isOpen,
    placeName,
  ])

  useIsomorphicLayoutEffect(() => {
    if (!drawerEntryId) return

    onPendingChange?.(drawerEntryId, isPending)

    return () => {
      onPendingChange?.(drawerEntryId, false)
    }
  }, [drawerEntryId, isPending, onPendingChange])

  if (!renderWhenReady) return null

  if (isPending) {
    if (!showImmediatePendingFeedback) return null

    return (
      <PendingPlaceDrawer
        isOpen={isOpen}
        onClose={onClose}
        onAfterClose={onAfterClose}
        onBack={onBack}
        animation={animation}
        placeName={placeName}
      />
    )
  }

  if (drawerClassification === 'destination') {
    return (
      <TripPlanDetailDrawer
        isOpen={isOpen}
        onDismiss={onClose}
        onAfterClose={onAfterClose}
        onBack={onBack}
        animation={animation}
        googlePlaceId={googlePlaceId}
        placeName={placeName}
        countryName={getCountryName(placeInfo)}
        chatId={chatId}
      />
    )
  }

  const placeInfoDrawerProps: ComponentProps<typeof PlaceInfoDrawer> = {
    isOpen,
    onClose,
    onAfterClose,
    onBack,
    animation,
    googlePlaceId,
    placeName,
    itineraryData,
    onSendMessage,
  }

  return <PlaceInfoDrawer {...placeInfoDrawerProps} />
}

export default SmartPlaceDrawer
